import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { first } from 'lodash';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import {
  DefensivoFormValue,
  ItemEstoque,
  ItemSaldo,
  TratamentoFormValue,
  TratamentoSementesFormValues,
  UnidadeConversao,
} from '../../../../@types';
import { defaultValues, FormDialog, validationSchema } from './form-controller';
import { useForm } from '../../../../components/Form/hooks';
import { ItensSaldoService, UnidadesService } from '../../../../services';
import useDropdownFilter from '../../../../hooks/use-dropdown-filter';
import {
  getPesoComPms,
  getTratamentoIdentification,
  humanizePesoComPms,
  isSegundoTratamento,
} from '../utils';
import Agro1Toast from '../../../../components/Agro1Toast';
import { FormObject } from '../../../../components/Form/types';
import Agro1Loader from '../../../../components/Agro1Loader';
import { CopiaDefensivo, HandleSementeOrigemParams } from './types';
import ItemSaldoOptionTemplate from '../../../../components/ItemSaldoOptionTemplate';

type Props = {
  listaSementesDeDestino: ItemEstoque[] | undefined;
  visible: boolean;
  onClose: () => void;
  onSubmit: (value: TratamentoFormValue) => void;
  selectedTratamentoId?: string;
  formObject: FormObject<TratamentoSementesFormValues>;
};

const TratamentoDeSementesParametrosFormDialog = ({
  listaSementesDeDestino,
  visible,
  onClose,
  onSubmit,
  selectedTratamentoId,
  formObject,
}: Props) => {
  const tratamentos = formObject.form.watch('tratamentos');

  const tratamento = useMemo(
    () =>
      tratamentos?.find((item) => item.identification === selectedTratamentoId),
    [selectedTratamentoId, tratamentos],
  );

  const [unidades, setUnidades] = useState<UnidadeConversao[]>([]);

  const [sementesDeDestino, setSementesDeDestino] = useState<ItemEstoque[]>([]);

  const { filterQuery: sementeFilterQuery, onFilter: onFilterSemente } =
    useDropdownFilter();
  const { filterQuery: defensivoFilterQuery, onFilter: onFilterDefensivo } =
    useDropdownFilter();

  const propriedade = formObject.form.watch('propriedade');

  const [sementesDeOrigem, setSementesDeOrigem] = useState<ItemSaldo[]>([]);
  const [loadingSementes, setLoadingSementes] = useState(false);
  const [defensivosDisponiveis, setDefensivosDisponiveis] = useState<
    ItemSaldo[]
  >([]);
  const [loadingDefensivos, setLoadingDefensivos] = useState(false);

  useEffect(() => {
    if (!propriedade) return;
    let cancelled = false;
    setLoadingSementes(true);
    setSementesDeOrigem([]);
    ItensSaldoService.saldos({
      operacaoItem: 'cultivar',
      propriedadeId: propriedade.id,
      sementeValidaTratamento: true,
      descricao: sementeFilterQuery,
    })
      .then((data) => {
        if (!cancelled) setSementesDeOrigem(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingSementes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propriedade, sementeFilterQuery]);

  useEffect(() => {
    if (!propriedade) return;
    let cancelled = false;
    setLoadingDefensivos(true);
    setDefensivosDisponiveis([]);
    ItensSaldoService.saldos({
      operacaoItem: 'pulverizacao',
      propriedadeId: propriedade.id,
      descricao: defensivoFilterQuery,
    })
      .then((data) => {
        if (!cancelled) setDefensivosDisponiveis(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingDefensivos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propriedade, defensivoFilterQuery]);

  const dialogForm = useForm<TratamentoFormValue>({
    validationSchema,
    defaultValues,
  });
  const initializedTratamentoIdRef = useRef<string>();

  const { setValue, watch, reset, resetField, handleSubmit, getValues } =
    dialogForm.form;
  const [
    dataInicio,
    unidade,
    defensivos,
    pms,
    quantidade,
    sementeOrigem,
    sementeDestino,
  ] = watch([
    'dataInicio',
    'unidade',
    'defensivos',
    'pms',
    'quantidade',
    'sementeOrigem',
    'sementeDestino',
  ]);

  const segundoTratamento = isSegundoTratamento({
    sementeOrigem,
    sementeDestino,
  });

  const filteredDefensivosDisponiveis = useMemo(() => {
    const ids = defensivos.map(
      (defensivo) => defensivo.item && defensivo.item.id,
    );
    return defensivosDisponiveis?.filter(
      (item) => !ids.some((id) => id === item.id),
    );
  }, [defensivos, defensivosDisponiveis]);

  const listaCopiaDefensivos: CopiaDefensivo[] = useMemo(
    () =>
      tratamentos
        .filter((item) => item.identification !== selectedTratamentoId)
        .map((item) => ({
          id: item.identification!,
          label: `${item.sementeOrigem?.descricao} >> ${item.sementeDestino?.descricao}`,
          defensivos: item.defensivos,
        })),
    [selectedTratamentoId, tratamentos],
  );

  const formattedTaxaSementes = useMemo(() => {
    if (!unidade) return '-';

    return formatNumber(unidade.taxaSementes, {
      maximumFractionDigits: 4,
      useGrouping: true,
    });
  }, [unidade]);

  const formattedPeso = useMemo(
    () =>
      humanizePesoComPms({
        pms,
        quantidade,
        unidade,
      }),
    [pms, quantidade, unidade],
  );

  const copyHandler = useCallback(
    (copiaDefensivo: CopiaDefensivo) => {
      resetField('search');
      resetField('copiarDeOutraSemente');
      setValue('defensivos', copiaDefensivo.defensivos);
    },
    [resetField, setValue],
  );

  const addDefensivo = useCallback(
    (item: ItemSaldo) => {
      const newDefensivo: DefensivoFormValue = {
        item,
        quantidade: undefined,
        dose: undefined,
      };
      const currentDefensivos = getValues('defensivos');
      setValue('defensivos', [...currentDefensivos, newDefensivo]);

      resetField('search');
    },
    [getValues, resetField, setValue],
  );

  const removeDefensivo = useCallback(
    (index: number) => {
      const currentDefensivos = getValues('defensivos');
      const newDefensivos = [...currentDefensivos];
      newDefensivos.splice(index, 1);
      setValue('defensivos', newDefensivos);
    },
    [getValues, setValue],
  );

  const fetchUnidades = useCallback(async (unidadeId: number) => {
    const loaderId = Agro1Loader.show();
    let newUnidades;
    try {
      newUnidades = await UnidadesService.listConversao(unidadeId);

      setUnidades(newUnidades || []);
    } catch (error) {
      console.error(error);
      Agro1Toast.show({
        detail: 'Falha ao carregar unidades.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }

    return newUnidades;
  }, []);

  const clearDadosDaSemente = useCallback(() => {
    setUnidades([]);
    resetField('unidade');
    resetField('quantidade');
    resetField('pms');
    resetField('peso');
  }, [resetField]);

  const handleSementeDeOrigem = useCallback(
    async ({
      sementeOrigem: novaSementeOrigem,
      sementeDestino: sementeDestinoSelecionada,
      unidadeId,
    }: HandleSementeOrigemParams) => {
      if (!listaSementesDeDestino || !novaSementeOrigem) return;

      try {
        const newSementesDeDestino = listaSementesDeDestino.filter(
          (item) =>
            item.codigoSementeOrigem === novaSementeOrigem.id ||
            (item.itemId === novaSementeOrigem.id && item.possuiTratamento),
        );

        setSementesDeDestino(newSementesDeDestino);

        let selectedSementeDeDestino = sementeDestinoSelecionada;

        if (selectedSementeDeDestino) {
          setValue('sementeDestino', selectedSementeDeDestino, {
            shouldValidate: true,
          });
        } else if (newSementesDeDestino.length <= 1) {
          selectedSementeDeDestino = first(newSementesDeDestino);

          setValue('sementeDestino', selectedSementeDeDestino, {
            shouldValidate: true,
          });
        } else {
          resetField('sementeDestino');
        }

        if (
          isSegundoTratamento({
            sementeOrigem: novaSementeOrigem,
            sementeDestino: selectedSementeDeDestino,
          })
        ) {
          clearDadosDaSemente();
          return;
        }

        const selectedUnidadeId =
          unidadeId || parseInt(novaSementeOrigem.unidade.id, 10);
        const newUnidades = await fetchUnidades(selectedUnidadeId);
        const [origemAtual, destinoAtual] = getValues([
          'sementeOrigem',
          'sementeDestino',
        ]);
        if (
          isSegundoTratamento({
            sementeOrigem: origemAtual,
            sementeDestino: destinoAtual,
          })
        ) {
          setUnidades([]);
          return;
        }

        const selectedUnidade = first(newUnidades);
        setValue('unidade', selectedUnidade, { shouldValidate: true });
      } catch (error) {
        console.error(error);
        Agro1Toast.show({
          detail: 'Erro ao processar a semente de origem',
          severity: 'error',
          sticky: true,
        });
      }
    },
    [
      clearDadosDaSemente,
      fetchUnidades,
      getValues,
      listaSementesDeDestino,
      resetField,
      setValue,
    ],
  );

  const handleSementeDeDestino = useCallback(
    async (novaSementeDestino: ItemEstoque) => {
      if (!sementeOrigem) return;

      if (
        isSegundoTratamento({
          sementeOrigem,
          sementeDestino: novaSementeDestino,
        })
      ) {
        clearDadosDaSemente();
        return;
      }

      const newUnidades = await fetchUnidades(
        parseInt(sementeOrigem.unidade.id, 10),
      );
      const [origemAtual, destinoAtual] = getValues([
        'sementeOrigem',
        'sementeDestino',
      ]);
      if (
        isSegundoTratamento({
          sementeOrigem: origemAtual,
          sementeDestino: destinoAtual,
        })
      ) {
        setUnidades([]);
        return;
      }

      setValue('unidade', first(newUnidades), { shouldValidate: true });
    },
    [clearDadosDaSemente, fetchUnidades, getValues, sementeOrigem, setValue],
  );

  const submitHandler = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { copiarDeOutraSemente, search, ...value } = getValues();
    const id = getTratamentoIdentification({
      defensivos: value.defensivos,
      sementeDestinoId: value.sementeDestino!.itemId,
      sementeOrigemId: value.sementeOrigem!.id,
    });

    const tratamentoJaExiste = tratamentos.some(
      (item) =>
        item.identification === id &&
        item.identification !== selectedTratamentoId,
    );

    if (tratamentoJaExiste) {
      Agro1Toast.show({
        detail:
          'Falha ao atualizar o registro. Já existe um tratamento para a semente e defensivo informados.',
        severity: 'error',
        sticky: true,
      });
      return;
    }

    const defensivosNormalizados = value.defensivos.map((defensivo) =>
      segundoTratamento
        ? { ...defensivo, dose: undefined }
        : { ...defensivo, quantidade: undefined },
    );

    onSubmit({
      ...value,
      defensivos: defensivosNormalizados,
      unidade: segundoTratamento ? undefined : unidade,
      quantidade: segundoTratamento ? undefined : value.quantidade,
      pms: segundoTratamento ? undefined : value.pms,
      peso: segundoTratamento ? undefined : value.peso,
      identification: id,
    });
    onClose();
  };

  useEffect(() => {
    if (segundoTratamento) {
      setValue('peso', undefined);
      return;
    }

    const newPeso = getPesoComPms({ unidade, quantidade, pms });

    setValue('peso', newPeso);
  }, [pms, unidade, quantidade, segundoTratamento, setValue]);

  useEffect(() => {
    if (!visible) {
      initializedTratamentoIdRef.current = undefined;
      reset(defaultValues);
      return;
    }

    const dialogId = selectedTratamentoId || 'novo-tratamento';
    if (initializedTratamentoIdRef.current === dialogId) return;

    initializedTratamentoIdRef.current = dialogId;

    if (!tratamento) {
      reset(defaultValues);
      return;
    }

    reset({ ...defaultValues, ...tratamento });

    if (tratamento.sementeOrigem && tratamento.unidade) {
      handleSementeDeOrigem({
        sementeOrigem: tratamento.sementeOrigem,
        sementeDestino: tratamento.sementeDestino,
        unidadeId: tratamento.unidade.id,
      });
    } else if (tratamento.sementeOrigem && tratamento.sementeDestino) {
      handleSementeDeOrigem({
        sementeOrigem: tratamento.sementeOrigem,
        sementeDestino: tratamento.sementeDestino,
      });
    }
  }, [visible, selectedTratamentoId, tratamento, reset, handleSementeDeOrigem]);

  return (
    <Dialog
      header={`${tratamento ? 'Editar' : 'Nova'} semente para tratamento`}
      headerClassName="text-gray text-lg"
      visible={visible}
      className="tratamento-de-sementes-parametros-form-dialog"
      onHide={onClose}
    >
      <FormDialog
        formObject={dialogForm}
        onSubmit={() => null}
        submitByEnter={false}
      >
        <div className="grid">
          <FormDialog.Calendar
            name="dataInicio"
            label="Data início:"
            containerClassName="col-3"
            className="w-full"
            addonRight={<i className="pi pi-calendar" />}
            onChange={() => resetField('dataFim')}
          />
          <FormDialog.Calendar
            name="dataFim"
            label="Data término:"
            containerClassName="col-3"
            className="w-full"
            addonRight={<i className="pi pi-calendar" />}
            minDate={dataInicio ?? undefined}
          />
          <FormDialog.Dropdown
            options={sementesDeOrigem}
            dataKey="id"
            optionLabel="descricao"
            label={
              <>
                Semente origem{' '}
                {segundoTratamento && (
                  <span className="segundo-tratamento-label">
                    (Segundo tratamento)
                  </span>
                )}
                :
              </>
            }
            name="sementeOrigem"
            containerClassName={segundoTratamento ? 'col-6' : 'col-3'}
            itemTemplate={ItemSaldoOptionTemplate}
            filter
            loading={loadingSementes}
            dropdownIcon={loadingSementes ? 'pi pi-spin pi-spinner' : undefined}
            onFilter={onFilterSemente}
            onChange={(event: DropdownChangeEvent) => {
              handleSementeDeOrigem({ sementeOrigem: event.target.value });
            }}
          />
          {!segundoTratamento && (
            <FormDialog.Dropdown
              options={sementesDeDestino}
              dataKey="itemId"
              optionLabel="descricao"
              label="Semente destino:"
              name="sementeDestino"
              containerClassName="col-3"
              disabled={sementesDeDestino.length <= 1}
              onChange={(event: DropdownChangeEvent) =>
                handleSementeDeDestino(event.target.value)
              }
            />
          )}
          {!segundoTratamento && (
            <>
              <FormDialog.Dropdown
                options={unidades}
                dataKey="id"
                optionLabel="descricao"
                label="Unidade:"
                name="unidade"
                containerClassName="field mb-0 mt-0 col-3"
              />
              <FormDialog.InputNumber
                label="Quantidade:"
                name="quantidade"
                containerClassName="col-3"
                className="w-full"
                addonRight={unidade?.abreviatura}
                maxFractionDigits={4}
              />
              {unidade && unidade.taxaSementes > 0 && (
                <>
                  <div className="col-3">
                    <p className="m-0 mb-2 bold-font-size">
                      Taxa de conversão de semente:
                    </p>
                    <p className="p-inputtext border-transparent px-0">
                      {formattedTaxaSementes}
                    </p>
                  </div>
                  <FormDialog.InputNumber
                    label="PMS (g):"
                    name="pms"
                    containerClassName="col-3"
                    className="w-full"
                    maxFractionDigits={4}
                  />
                  <div className="col-3">
                    <p className="m-0 mb-2 bold-font-size">Peso (kg):</p>
                    <p className="p-inputtext border-transparent px-0">
                      {formattedPeso}
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <h3 className="text-gray text-sm m-0 mt-3">Defensivos</h3>
        <hr className="mb-4 w-full" />

        <div className="grid">
          <FormDialog.Dropdown
            options={filteredDefensivosDisponiveis}
            dataKey="id"
            optionLabel="descricao"
            label="Pesquisar itens:"
            name="search"
            containerClassName="col-5"
            filter
            loading={loadingDefensivos}
            dropdownIcon={
              loadingDefensivos ? 'pi pi-spin pi-spinner' : undefined
            }
            onFilter={onFilterDefensivo}
            onChange={(event: DropdownChangeEvent) =>
              addDefensivo(event.target.value)
            }
            itemTemplate={ItemSaldoOptionTemplate}
          />

          {listaCopiaDefensivos.length > 0 && (
            <FormDialog.Dropdown
              options={listaCopiaDefensivos}
              dataKey="identification"
              optionLabel="label"
              label="Copiar defensivos de outro tratamento:"
              name="copiarDeOutraSemente"
              containerClassName="col-5"
              onChange={(event: DropdownChangeEvent) =>
                copyHandler(event.target.value)
              }
            />
          )}

          <div className="col-10 flex flex-column gap-2">
            {defensivos?.length > 0 && <hr className="w-full m-0" />}
            {defensivos?.map(
              ({ item }, index) =>
                item && (
                  <>
                    <div
                      key={item.id}
                      className="flex w-full align-items-center gap-4 px-3"
                    >
                      <div className="defensivo-content grid align-itens-center w-full">
                        <p className="label defensivo m-0 col-5">
                          {item.descricao}
                        </p>
                        <FormDialog.InputNumber
                          // Os types dos nossos formulários não estão prontos para receber names mais complexos,
                          // então isso fica como any temporariamente até conseguirmos resolver
                          // Referência: https://dev.to/tipsy_dev/advanced-typescript-reinventing-lodash-get-4fhe
                          name={
                            `defensivos[${index}].${
                              segundoTratamento ? 'quantidade' : 'dose'
                            }` as any
                          }
                          containerClassName="dose col-4"
                          className="w-full"
                          maxFractionDigits={4}
                        />
                        <p className="label unidade m-0 col-3">
                          {item.unidade.abreviatura.trim()}
                          {!segundoTratamento && '/100kg'}
                        </p>
                      </div>
                      <Button
                        className="button-danger"
                        type="button"
                        onClick={() => removeDefensivo(index)}
                        icon="pi pi-trash"
                      />
                    </div>
                    <hr className="w-full m-0" />
                  </>
                ),
            )}
          </div>
        </div>

        <div className="flex justify-content-end mt-5">
          <Button
            label={tratamento ? 'Salvar' : 'Adicionar'}
            disabled={defensivos?.length === 0}
            className="button-secondary"
            severity="success"
            type="button"
            onClick={() => handleSubmit(submitHandler)()}
          />
        </div>
      </FormDialog>
    </Dialog>
  );
};

export default TratamentoDeSementesParametrosFormDialog;
